import React, { useState, useEffect } from 'react';
import { useApp } from '../contexts/AppContext';
import axios from 'axios';
import { toast } from 'sonner';
import { BookOpen, Loader2, Save, Bot, FileText, Plus, Play, ChevronRight, Lightbulb, Sparkles, List, Share2, X } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SUBJECTS = [
  { bn: 'বাংলা', en: 'Bangla' }, { bn: 'English', en: 'English' },
  { bn: 'গণিত', en: 'Mathematics' }, { bn: 'বিজ্ঞান', en: 'Science' },
  { bn: 'সমাজবিজ্ঞান', en: 'Social Science' }, { bn: 'ধর্ম ও নৈতিক শিক্ষা', en: 'Religion' },
  { bn: 'তথ্য প্রযুক্তি', en: 'ICT' },
];

const AI_DEMO_STEPS = [
  { title: 'Select Grade & Subject', titleBn: 'শ্রেণি ও বিষয় নির্বাচন করুন', desc: 'Choose the grade level (1-12) and subject from the dropdowns. The AI uses this to align with NCTB curriculum.', descBn: 'ড্রপডাউন থেকে শ্রেণি (১-১২) এবং বিষয় নির্বাচন করুন। AI এটি NCTB পাঠ্যক্রমের সাথে মেলানোর জন্য ব্যবহার করে।' },
  { title: 'Add Chapter & Objective', titleBn: 'অধ্যায় ও উদ্দেশ্য যোগ করুন', desc: 'Optionally specify the chapter name and learning objective. More detail = better AI output.', descBn: 'ঐচ্ছিকভাবে অধ্যায়ের নাম এবং শিখন উদ্দেশ্য নির্দিষ্ট করুন। বেশি বিবরণ = ভালো AI ফলাফল।' },
  { title: 'Click "Generate with AI"', titleBn: '"AI দিয়ে তৈরি করুন" ক্লিক করুন', desc: 'The AI generates a structured plan with: Objectives, Materials, Introduction (10 min), Main Activity (25 min), Assessment (10 min), Homework, and Differentiation notes.', descBn: 'AI একটি কাঠামোবদ্ধ পরিকল্পনা তৈরি করে: উদ্দেশ্য, উপকরণ, পরিচিতি (১০ মিনিট), মূল কার্যক্রম (২৫ মিনিট), মূল্যায়ন (১০ মিনিট), বাড়ির কাজ, এবং বিশেষ চাহিদার নোট।' },
  { title: 'Review & Edit', titleBn: 'পর্যালোচনা ও সম্পাদনা করুন', desc: 'The AI plan is a draft — review it, make changes, add your own ideas. The AI disclaimer will always be visible.', descBn: 'AI পরিকল্পনা একটি খসড়া — এটি পর্যালোচনা করুন, পরিবর্তন করুন, আপনার নিজের ধারণা যোগ করুন।' },
  { title: 'Save to My Plans', titleBn: 'আমার পরিকল্পনায় সংরক্ষণ করুন', desc: 'Click "Save to My Plans" to store it. You can later share it with your school or district.', descBn: '"আমার পরিকল্পনায় সংরক্ষণ করুন" ক্লিক করে সংরক্ষণ করুন। পরে বিদ্যালয় বা জেলায় শেয়ার করতে পারবেন।' },
];

export default function LessonPlanner() {
  const { t, lang, currentTeacher, currentSchool } = useApp();
  const [view, setView] = useState('home'); // home, create, plans, demo
  const [form, setForm] = useState({ grade: '', subject: '', chapter: '', objective: '' });
  const [generatedPlan, setGeneratedPlan] = useState('');
  const [generating, setGenerating] = useState(false);
  const [saved, setSaved] = useState(false);
  const [plans, setPlans] = useState([]);
  const [demoStep, setDemoStep] = useState(0);

  useEffect(() => {
    if (currentTeacher) {
      axios.get(`${API}/lesson-plans`, { params: { teacher_id: currentTeacher.teacher_id } })
        .then(r => setPlans(r.data)).catch(() => {});
    }
  }, [currentTeacher, saved]);

  const generate = async () => {
    if (!form.grade || !form.subject) {
      toast.error(lang === 'bn' ? 'শ্রেণি ও বিষয় নির্বাচন করুন' : 'Select grade and subject');
      return;
    }
    setGenerating(true); setGeneratedPlan(''); setSaved(false);
    try {
      const res = await axios.post(`${API}/lesson-plans/generate`, {
        teacher_id: currentTeacher?.teacher_id || 'demo', grade: parseInt(form.grade),
        subject: form.subject, chapter: form.chapter || null, learning_objective: form.objective || null,
      });
      setGeneratedPlan(res.data.plan || res.data.content || '');
    } catch (e) {
      toast.error(lang === 'bn' ? 'AI সেবা সাময়িকভাবে অনুপলব্ধ' : 'AI service temporarily unavailable');
    }
    setGenerating(false);
  };

  const savePlan = async () => {
    if (!generatedPlan) return;
    try {
      await axios.post(`${API}/lesson-plans`, {
        teacher_id: currentTeacher?.teacher_id || 'demo', school_id: currentSchool?.school_id || 'demo',
        title_bangla: `${form.subject} - ${lang === 'bn' ? 'শ্রেণি' : 'Grade'} ${form.grade}${form.chapter ? ` - ${form.chapter}` : ''}`,
        grade: parseInt(form.grade), subject: form.subject, chapter: form.chapter || null,
        content: generatedPlan, ai_generated: true,
      });
      toast.success(lang === 'bn' ? 'পরিকল্পনা সংরক্ষিত' : 'Plan saved'); setSaved(true);
    } catch (e) { toast.error('Failed to save'); }
  };

  // ═══ HOME VIEW — Landing page with two main actions ═══
  if (view === 'home') {
    return (
      <div className="space-y-6" data-testid="lesson-planner-home">
        {/* Header */}
        <div style={{ paddingTop: 'var(--space-4)' }}>
          <h1 style={{ fontSize: 'var(--text-title2)', fontWeight: 700, letterSpacing: '-0.02em', color: 'var(--color-label-primary)' }}>
            {lang === 'bn' ? 'পাঠ পরিকল্পনা' : 'Lesson Plans'}
          </h1>
          <p style={{ fontSize: 'var(--text-subhead)', color: 'var(--color-label-secondary)', marginTop: 'var(--space-2)' }}>
            {lang === 'bn' ? 'NCTB পাঠ্যক্রম অনুযায়ী AI-সহায়তায় পাঠ পরিকল্পনা তৈরি ও পরিচালনা করুন' : 'Create and manage AI-assisted lesson plans aligned with NCTB curriculum'}
          </p>
        </div>

        {/* Two main action cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {/* Access Lesson Plans */}
          <button data-testid="access-lesson-plans-btn" onClick={() => setView('plans')}
            className="card-surface text-left" style={{ padding: 'var(--space-8)', cursor: 'pointer' }}>
            <div style={{ width: 48, height: 48, borderRadius: 'var(--radius-md)', background: 'var(--color-accent-light)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 'var(--space-4)' }}>
              <FileText size={24} style={{ color: 'var(--color-accent)' }} />
            </div>
            <h3 style={{ fontSize: 'var(--text-title3)', fontWeight: 600, color: 'var(--color-label-primary)', marginBottom: 'var(--space-2)' }}>
              {lang === 'bn' ? 'পাঠ পরিকল্পনা দেখুন' : 'Access Lesson Plans'}
            </h3>
            <p style={{ fontSize: 'var(--text-subhead)', color: 'var(--color-label-secondary)' }}>
              {lang === 'bn' ? 'আপনার সংরক্ষিত পরিকল্পনা ও বিদ্যালয় পাঠাগার দেখুন' : 'View your saved plans and school library'}
            </p>
            <div className="flex items-center gap-1 mt-4" style={{ color: 'var(--color-accent)', fontSize: 'var(--text-subhead)', fontWeight: 500 }}>
              <span>{plans.length} {lang === 'bn' ? 'টি পরিকল্পনা' : 'plans saved'}</span>
              <ChevronRight size={16} />
            </div>
          </button>

          {/* Develop a Lesson Plan */}
          <button data-testid="develop-lesson-plan-btn" onClick={() => setView('create')}
            className="card-surface text-left" style={{ padding: 'var(--space-8)', cursor: 'pointer', borderTop: '3px solid var(--color-accent)' }}>
            <div style={{ width: 48, height: 48, borderRadius: 'var(--radius-md)', background: 'var(--color-red-light)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 'var(--space-4)' }}>
              <Plus size={24} style={{ color: 'var(--color-red)' }} />
            </div>
            <h3 style={{ fontSize: 'var(--text-title3)', fontWeight: 600, color: 'var(--color-label-primary)', marginBottom: 'var(--space-2)' }}>
              {lang === 'bn' ? 'পাঠ পরিকল্পনা তৈরি করুন' : 'Develop a Lesson Plan'}
            </h3>
            <p style={{ fontSize: 'var(--text-subhead)', color: 'var(--color-label-secondary)' }}>
              {lang === 'bn' ? 'AI সহায়তায় নতুন পাঠ পরিকল্পনা তৈরি করুন' : 'Create a new lesson plan with AI assistance'}
            </p>
            <div className="flex items-center gap-1 mt-4" style={{ color: 'var(--color-red)', fontSize: 'var(--text-subhead)', fontWeight: 500 }}>
              <Sparkles size={16} />
              <span>{lang === 'bn' ? 'AI-সহায়তা উপলব্ধ' : 'AI-powered'}</span>
            </div>
          </button>
        </div>

        {/* AI Demo Tutorial Card */}
        <div className="card-surface" data-testid="ai-demo-tutorial" style={{ borderLeft: '4px solid var(--color-red)' }}>
          <div className="flex items-start gap-4">
            <div style={{ width: 44, height: 44, borderRadius: 'var(--radius-md)', background: 'var(--color-red-light)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <Lightbulb size={22} style={{ color: 'var(--color-red)' }} />
            </div>
            <div style={{ flex: 1 }}>
              <div className="flex items-center justify-between">
                <h3 style={{ fontSize: 'var(--text-body)', fontWeight: 600, color: 'var(--color-label-primary)' }}>
                  {lang === 'bn' ? 'AI দিয়ে পাঠ পরিকল্পনা তৈরি করুন — ডেমো' : 'How to Use AI to Develop Lesson Plans — Demo'}
                </h3>
                <button data-testid="start-ai-demo" onClick={() => { setView('demo'); setDemoStep(0); }}
                  className="btn btn-sm" style={{ background: 'var(--color-red)', color: 'white', padding: '6px 14px', fontSize: 'var(--text-footnote)' }}>
                  <Play size={14} />
                  {lang === 'bn' ? 'ডেমো দেখুন' : 'Watch Demo'}
                </button>
              </div>
              <p style={{ fontSize: 'var(--text-subhead)', color: 'var(--color-label-secondary)', marginTop: 'var(--space-2)', lineHeight: 1.6 }}>
                {lang === 'bn'
                  ? 'এই ৫-ধাপের টিউটোরিয়ালে শিখুন কিভাবে AI ব্যবহার করে মিনিটের মধ্যে NCTB পাঠ্যক্রম অনুযায়ী পাঠ পরিকল্পনা তৈরি করতে হয়।'
                  : 'Learn how to create NCTB-aligned lesson plans in minutes using AI. This 5-step tutorial walks you through the entire process from selecting your subject to saving your plan.'}
              </p>
              {/* Quick step preview */}
              <div className="flex gap-2 mt-3 flex-wrap">
                {AI_DEMO_STEPS.map((step, i) => (
                  <span key={i} style={{ fontSize: 'var(--text-caption)', padding: '2px 8px', borderRadius: 'var(--radius-pill)',
                    background: 'var(--color-surface-2)', color: 'var(--color-label-secondary)' }}>
                    {i + 1}. {lang === 'bn' ? step.titleBn.split(' ').slice(0, 2).join(' ') : step.title.split(' ').slice(0, 2).join(' ')}...
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Recent Plans preview */}
        {plans.length > 0 && (
          <div className="card-surface">
            <div className="flex items-center justify-between mb-4">
              <h3 style={{ fontSize: 'var(--text-subhead)', fontWeight: 600, color: 'var(--color-label-secondary)' }}>
                {lang === 'bn' ? 'সাম্প্রতিক পরিকল্পনা' : 'Recent Plans'}
              </h3>
              <button onClick={() => setView('plans')} style={{ fontSize: 'var(--text-subhead)', color: 'var(--color-accent)', fontWeight: 500, background: 'none', border: 'none', cursor: 'pointer' }}>
                {lang === 'bn' ? 'সব দেখুন' : 'View All'} →
              </button>
            </div>
            {plans.slice(0, 3).map(p => (
              <div key={p.plan_id} className="list-row" style={{ borderRadius: 'var(--radius-sm)', marginBottom: 'var(--space-2)' }}>
                <div className="list-row-icon"><FileText size={16} style={{ color: 'var(--color-accent)' }} /></div>
                <div style={{ flex: 1 }}>
                  <p style={{ fontSize: 'var(--text-subhead)', fontWeight: 500, color: 'var(--color-label-primary)' }}>{p.title_bangla}</p>
                  <p style={{ fontSize: 'var(--text-footnote)', color: 'var(--color-label-tertiary)' }}>
                    {p.subject} | {lang === 'bn' ? 'শ্রেণি' : 'Grade'} {p.grade}
                    {p.ai_generated && <span style={{ color: 'var(--color-warning)', marginLeft: 6 }}><Bot size={10} style={{ display: 'inline', verticalAlign: 'middle' }} /> AI</span>}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  // ═══ DEMO VIEW — Step-by-step AI tutorial ═══
  if (view === 'demo') {
    const step = AI_DEMO_STEPS[demoStep];
    return (
      <div className="space-y-6" data-testid="lesson-planner-demo">
        <div className="flex items-center justify-between">
          <button onClick={() => setView('home')} style={{ color: 'var(--color-label-secondary)', background: 'none', border: 'none', cursor: 'pointer', fontSize: 'var(--text-subhead)' }}>
            ← {lang === 'bn' ? 'ফিরে যান' : 'Back'}
          </button>
          <span style={{ fontSize: 'var(--text-footnote)', color: 'var(--color-label-tertiary)' }}>
            {lang === 'bn' ? 'ধাপ' : 'Step'} {demoStep + 1}/{AI_DEMO_STEPS.length}
          </span>
        </div>

        {/* Progress bar */}
        <div className="progress-track" style={{ height: 6 }}>
          <div className="progress-fill" style={{ width: `${((demoStep + 1) / AI_DEMO_STEPS.length) * 100}%` }} />
        </div>

        {/* Step content */}
        <div className="card-surface" style={{ padding: 'var(--space-10)', textAlign: 'center' }}>
          <div style={{ width: 64, height: 64, borderRadius: '50%', background: 'var(--color-accent-light)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto var(--space-6)' }}>
            <span style={{ fontSize: 28, fontWeight: 700, color: 'var(--color-accent)' }}>{demoStep + 1}</span>
          </div>
          <h2 style={{ fontSize: 'var(--text-title2)', fontWeight: 700, color: 'var(--color-label-primary)', marginBottom: 'var(--space-4)' }}>
            {lang === 'bn' ? step.titleBn : step.title}
          </h2>
          <p style={{ fontSize: 'var(--text-body)', color: 'var(--color-label-secondary)', lineHeight: 1.7, maxWidth: 560, margin: '0 auto' }}>
            {lang === 'bn' ? step.descBn : step.desc}
          </p>
        </div>

        {/* Navigation */}
        <div className="flex items-center justify-between">
          <button onClick={() => setDemoStep(Math.max(0, demoStep - 1))}
            className="btn btn-secondary btn-sm" disabled={demoStep === 0}>
            ← {lang === 'bn' ? 'আগের' : 'Previous'}
          </button>
          {demoStep < AI_DEMO_STEPS.length - 1 ? (
            <button onClick={() => setDemoStep(demoStep + 1)}
              className="btn btn-sm" style={{ background: 'var(--color-accent)', color: 'white' }}>
              {lang === 'bn' ? 'পরবর্তী' : 'Next'} →
            </button>
          ) : (
            <button onClick={() => setView('create')}
              className="btn btn-sm" style={{ background: 'var(--color-red)', color: 'white' }}>
              <Sparkles size={14} />
              {lang === 'bn' ? 'এখন তৈরি করুন' : 'Create Now'}
            </button>
          )}
        </div>
      </div>
    );
  }

  // ═══ PLANS VIEW — List of saved plans ═══
  if (view === 'plans') {
    return (
      <div className="space-y-4" data-testid="lesson-plans-list">
        <div className="flex items-center justify-between">
          <button onClick={() => setView('home')} style={{ color: 'var(--color-label-secondary)', background: 'none', border: 'none', cursor: 'pointer', fontSize: 'var(--text-subhead)' }}>
            ← {lang === 'bn' ? 'ফিরে যান' : 'Back'}
          </button>
          <button onClick={() => setView('create')} className="btn btn-sm" style={{ background: 'var(--color-accent)', color: 'white' }}>
            <Plus size={14} /> {lang === 'bn' ? 'নতুন পরিকল্পনা' : 'New Plan'}
          </button>
        </div>

        <h2 style={{ fontSize: 'var(--text-title3)', fontWeight: 600, color: 'var(--color-label-primary)' }}>
          {lang === 'bn' ? 'আমার পাঠ পরিকল্পনা' : 'My Lesson Plans'} ({plans.length})
        </h2>

        {plans.length === 0 ? (
          <div className="card-surface" style={{ textAlign: 'center', padding: 'var(--space-12)' }}>
            <BookOpen size={48} style={{ color: 'var(--color-label-tertiary)', margin: '0 auto 16px' }} />
            <p style={{ color: 'var(--color-label-secondary)' }}>{lang === 'bn' ? 'কোনো পরিকল্পনা পাওয়া যায়নি' : 'No lesson plans yet'}</p>
            <button onClick={() => setView('create')} className="btn btn-sm mt-4" style={{ background: 'var(--color-accent)', color: 'white' }}>
              {lang === 'bn' ? 'প্রথম পরিকল্পনা তৈরি করুন' : 'Create Your First Plan'}
            </button>
          </div>
        ) : (
          <div className="card-surface" style={{ padding: 0, overflow: 'hidden' }}>
            {plans.map((p, i) => (
              <div key={p.plan_id} className="list-row" style={{ padding: 'var(--space-4) var(--space-5)' }}>
                <div className="list-row-icon"><FileText size={16} style={{ color: 'var(--color-accent)' }} /></div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ fontSize: 'var(--text-body)', fontWeight: 500, color: 'var(--color-label-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.title_bangla}</p>
                  <p style={{ fontSize: 'var(--text-footnote)', color: 'var(--color-label-tertiary)', marginTop: 2 }}>
                    {p.subject} | {lang === 'bn' ? 'শ্রেণি' : 'Grade'} {p.grade}
                    {p.ai_generated && <span style={{ marginLeft: 8, color: 'var(--color-warning)' }}>AI</span>}
                    {p.shared_to_school && <span style={{ marginLeft: 8 }}><Share2 size={10} style={{ display: 'inline', color: 'var(--color-accent)' }} /> shared</span>}
                  </p>
                </div>
                <span style={{ fontSize: 'var(--text-caption)', color: 'var(--color-label-quarternary)' }}>
                  {new Date(p.created_at).toLocaleDateString()}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  // ═══ CREATE VIEW — AI Lesson Plan Generator ═══
  return (
    <div className="space-y-4" data-testid="lesson-planner-create">
      <div className="flex items-center justify-between">
        <button onClick={() => setView('home')} style={{ color: 'var(--color-label-secondary)', background: 'none', border: 'none', cursor: 'pointer', fontSize: 'var(--text-subhead)' }}>
          ← {lang === 'bn' ? 'ফিরে যান' : 'Back'}
        </button>
      </div>

      <div>
        <h2 style={{ fontSize: 'var(--text-title3)', fontWeight: 600, color: 'var(--color-label-primary)' }}>
          {lang === 'bn' ? 'নতুন পাঠ পরিকল্পনা তৈরি করুন' : 'Create New Lesson Plan'}
        </h2>
        <p style={{ fontSize: 'var(--text-subhead)', color: 'var(--color-label-secondary)', marginTop: 'var(--space-1)' }}>
          {lang === 'bn' ? 'AI দিয়ে NCTB পাঠ্যক্রম অনুযায়ী পাঠ পরিকল্পনা তৈরি করুন' : 'Generate a structured lesson plan with AI, aligned to NCTB curriculum'}
        </p>
      </div>

      <div className="card-surface space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label style={{ fontSize: 'var(--text-footnote)', fontWeight: 500, color: 'var(--color-label-secondary)', display: 'block', marginBottom: 'var(--space-2)' }}>{lang === 'bn' ? 'শ্রেণি' : 'Grade'}</label>
            <select data-testid="lesson-grade" value={form.grade} onChange={e => setForm({ ...form, grade: e.target.value })}
              className="input-filled" style={{ fontSize: 'var(--text-subhead)' }}>
              <option value="">--</option>
              {Array.from({ length: 12 }, (_, i) => i + 1).map(g => <option key={g} value={g}>{lang === 'bn' ? `শ্রেণি ${g}` : `Grade ${g}`}</option>)}
            </select>
          </div>
          <div>
            <label style={{ fontSize: 'var(--text-footnote)', fontWeight: 500, color: 'var(--color-label-secondary)', display: 'block', marginBottom: 'var(--space-2)' }}>{lang === 'bn' ? 'বিষয়' : 'Subject'}</label>
            <select data-testid="lesson-subject" value={form.subject} onChange={e => setForm({ ...form, subject: e.target.value })}
              className="input-filled" style={{ fontSize: 'var(--text-subhead)' }}>
              <option value="">--</option>
              {SUBJECTS.map(s => <option key={s.bn} value={s.bn}>{lang === 'bn' ? s.bn : s.en}</option>)}
            </select>
          </div>
        </div>
        <div>
          <label style={{ fontSize: 'var(--text-footnote)', fontWeight: 500, color: 'var(--color-label-secondary)', display: 'block', marginBottom: 'var(--space-2)' }}>{lang === 'bn' ? 'অধ্যায়' : 'Chapter'} ({lang === 'bn' ? 'ঐচ্ছিক' : 'optional'})</label>
          <input data-testid="lesson-chapter" type="text" value={form.chapter} onChange={e => setForm({ ...form, chapter: e.target.value })}
            className="input-filled" placeholder={lang === 'bn' ? 'অধ্যায়ের নাম' : 'Chapter name'} />
        </div>
        <div>
          <label style={{ fontSize: 'var(--text-footnote)', fontWeight: 500, color: 'var(--color-label-secondary)', display: 'block', marginBottom: 'var(--space-2)' }}>{lang === 'bn' ? 'শিখন উদ্দেশ্য' : 'Learning Objective'} ({lang === 'bn' ? 'ঐচ্ছিক' : 'optional'})</label>
          <textarea data-testid="lesson-objective" value={form.objective} onChange={e => setForm({ ...form, objective: e.target.value })}
            className="input-filled" rows={2} placeholder={lang === 'bn' ? 'শিখন উদ্দেশ্য লিখুন' : 'What should students learn?'} />
        </div>

        <button data-testid="generate-lesson-plan" onClick={generate} disabled={generating}
          className="btn w-full" style={{ background: 'var(--color-accent)', color: 'white', fontSize: 'var(--text-body)' }}>
          {generating ? (<><Loader2 size={18} className="animate-spin" />{lang === 'bn' ? 'AI তৈরি করছে...' : 'AI is generating...'}</>)
            : (<><Bot size={18} />{lang === 'bn' ? 'AI দিয়ে তৈরি করুন' : 'Generate with AI'}</>)}
        </button>
      </div>

      {/* Generated Plan */}
      {generatedPlan && (
        <div className="card-surface space-y-4" data-testid="generated-plan">
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', padding: 'var(--space-3) var(--space-4)', background: 'var(--color-warning-bg)', borderRadius: 'var(--radius-sm)', borderLeft: '4px solid var(--color-warning)' }}>
            <Bot size={16} style={{ color: 'var(--color-warning)' }} />
            <span style={{ fontSize: 'var(--text-footnote)', color: 'var(--color-warning)', fontWeight: 500 }}>
              {lang === 'bn' ? 'AI সহায়তায় তৈরি — শিক্ষক পর্যালোচনা করুন' : 'AI-assisted draft — please review and edit'}
            </span>
          </div>
          <div style={{ fontSize: 'var(--text-subhead)', color: 'var(--color-label-primary)', whiteSpace: 'pre-wrap', lineHeight: 1.7 }}>
            {generatedPlan}
          </div>
          {!saved ? (
            <button data-testid="save-lesson-plan" onClick={savePlan}
              className="btn w-full" style={{ background: 'var(--color-accent)', color: 'white' }}>
              <Save size={16} /> {lang === 'bn' ? 'আমার পরিকল্পনায় সংরক্ষণ করুন' : 'Save to My Plans'}
            </button>
          ) : (
            <div style={{ textAlign: 'center', padding: 'var(--space-4)', background: 'var(--color-success-bg)', borderRadius: 'var(--radius-sm)', color: 'var(--color-success)', fontSize: 'var(--text-subhead)' }}>
              {lang === 'bn' ? 'পরিকল্পনা সংরক্ষিত হয়েছে' : 'Plan saved successfully'}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
